import Button from "@/atoms/Button";
import Input from "@/atoms/Input";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { landlordsSignUpSchema } from "@/helpers/validators";
import { LandlordsSignUpForm, Props } from "./types";
import Close from "@/public/icons/close-simple-icon.svg";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import { createLandlordMondayID } from "@/services/api";
import Checkbox from "@/atoms/Checkbox";

const SignUpModalBody = ({
  setShowModal,
  setLandlordMondayID,
  setPopupIndex,
  showModal,
}: Props) => {
  const [isMutating, setIsMutating] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isReset, setIsReset] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    reset,
    clearErrors,
  } = useForm<LandlordsSignUpForm>({
    resolver: yupResolver(landlordsSignUpSchema),
    mode: "onTouched",
  });

  const onChange = () => {
    setChecked((checked) => !checked);
  };

  const mobileNumberWatch = watch("mobile_number");

  const { mutate } = useMutation(createLandlordMondayID, {
    onSuccess: (data) => {
      setLandlordMondayID(data.monday_id);
      setPopupIndex(1);
    },
    onError: (err: AxiosError) => {
      console.log("Error:", err);
    },
    onMutate: () => setIsMutating(true),
    onSettled: () => setIsMutating(false),
  });

  const onSubmit = async (data: any) => {
    mutate({
      data: {
        full_name: data.full_name.trim(),
        email: data.email.trim(),
        mobile_number: data.mobile_number.replace("(0) ", ""),
      },
    });
  };

  const handleCloseAndResetModal = () => {
    setShowModal(false);
    setPopupIndex(0);
    setLandlordMondayID("");
    reset({
      full_name: undefined,
      email: undefined,
      mobile_number: undefined,
    });
  };

  useEffect(() => {
    clearErrors();
  }, [showModal, clearErrors]);

  return (
    <>
      <div>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
          <div className="flex flex-col mx-auto mb-4 md:mb-10">
            <div className="form-container !px-3 md:!px-8 !pt-4 md:!pt-8">
              <Input
                type="text"
                label="Full name"
                placeholder="e.g. Julie Smith"
                {...register("full_name", { required: true })}
                success={!!getValues("full_name") && !errors.full_name}
                error={errors.full_name ? errors.full_name.message : ""}
              />
              <div>
                <Input
                  type="text"
                  label="Email address"
                  placeholder="e.g. julie.smith@mail.com"
                  value=""
                  {...register("email")}
                  disabled={false}
                  success={!errors.email && !!getValues("email")}
                  error={errors.email ? errors.email.message : ""}
                />
              </div>
              <div className="md:w-64 mb-8">
                <Input
                  type="text"
                  label="Mobile number"
                  format="+44 (0) ####-######"
                  mask="_"
                  allowEmptyFormatting
                  isNumericString={false}
                  value={getValues("mobile_number")}
                  {...register("mobile_number")}
                  className={`${
                    !(
                      !!getValues("mobile_number") &&
                      getValues("mobile_number") !== "+44 (0) ____-______"
                    ) && "text-grey-500"
                  }`}
                  success={
                    !errors.mobile_number &&
                    !!mobileNumberWatch &&
                    mobileNumberWatch !== "+44 (0) ____-______" &&
                    mobileNumberWatch?.replaceAll("_", "").length > 18
                  }
                  error={(errors.mobile_number && errors.mobile_number.message) || ""}
                  noMargin
                />
              </div>
              <Checkbox
                checked={checked}
                onChange={onChange}
                id="consent"
                checkboxTheme="light"
                checkboxFontSize="lg"
              >
                By registering with Keyzy for Landlords, I acknowledge that I have read and
                understood Keyzy{" "}
                <a className="underline" href={"/terms-of-use"} target="_blank" rel="noreferrer">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href={"/privacy"} className="underline" target="_blank" rel="noreferrer">
                  Privacy Notice
                </a>
                .
              </Checkbox>
            </div>

            <div className="flex flex-col md:flex-row justify-between mt-8">
              <Button
                onClick={handleCloseAndResetModal}
                variant={"outline"}
                suffix={<Close />}
                type="button"
                className="xl:order-1 order-2 "
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isMutating}
                disabled={!checked}
                className="ml-auto xl:order-2 order-1"
              >
                Sign up
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default SignUpModalBody;
